import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #selectionGeneration = 0;
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    this.#previewPrice(event.target);

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        const newCache = newProductPrice.querySelector('[data-tw-price-cache]');
        this.querySelector('[data-tw-price-cache]')?.remove();
        if (newCache) this.append(newCache.cloneNode(true));

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
  #previewPrice(target) {
    const picker = target.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId) return;
    const choices = Array.from(picker.querySelectorAll('fieldset input:checked, select'));
    const selected = choices.map((choice) =>
      choice instanceof HTMLSelectElement ? choice.selectedOptions[0] : choice
    );
    const productPath = picker.dataset.productUrl?.split('?')[0];
    if (selected.some((choice) => choice?.dataset.connectedProductUrl && choice.dataset.connectedProductUrl !== productPath)) return;
    const values = selected.map((choice) => choice?.value);
    if (!values.length || values.some((value) => value === undefined)) return;

    const update = (host) => {
      const cache = host.querySelector('[data-tw-price-cache]');
      if (cache?.dataset.productId !== this.dataset.productId) return;
      const template = Array.from(cache.querySelectorAll('template')).find((item) => {
        try {
          const options = JSON.parse(item.dataset.options || '[]');
          return options.length === values.length && options.every((value, index) => value === values[index]);
        } catch {
          return false;
        }
      });
      const price = template?.content.querySelector('[ref="priceContainer"]');
      const current = host.querySelector('[ref="priceContainer"]');
      if (price && current) current.replaceWith(price.cloneNode(true));
      return template;
    };

    update(this);
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    if (sticky?.dataset.productId === this.dataset.productId) {
      const template = update(sticky);
      const title = sticky.querySelector('.sticky-add-to-cart__variant');
      if (template && title) title.textContent = template.dataset.variantTitle || '';
    }
  }

}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

